/* ==========================================================================
   Endure · Liquid Glass engine  v2
   --------------------------------------------------------------------------
   True glassmorphic refraction, tuned to the Apple/Reddit "Liquid Glass" feel.

   What makes it real (vs. a frosted div):
   1. CONCAVE LENS — every glass element gets its OWN displacement map, drawn
      from a rounded-rect signed-distance field. The centre is undistorted
      (clear glass); the rim bends light inward, magnifying what's behind it.
   2. CHROMATIC ABERRATION — the R/G/B channels are displaced by slightly
      different amounts, so the lensed rim splits into colour (the rainbow edge
      on the Reddit selector chip). Most visible where glass overlaps content.
   3. REFRACTED ACCENT LINE — colour lives as a bright LINE at the bottom of an
      otherwise-clear button. It sits on a filtered layer, so the same lens
      bends it UP through the glass with the same rainbow fringing.
   4. ADAPTIVE OPACITY — the backdrop is pixel-sampled (images/video included);
      glass goes clearer over dark content, more opaque over light.
   5. HOVER CHIP — a brighter, stronger lens that appears on hover and (in a
      nav) slides between items as a selector, refracting the content beneath.

   JS only ever writes CSS custom properties + swaps a filter href, so paint
   stays on the compositor.
   ========================================================================== */

const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const COARSE = window.matchMedia('(pointer: coarse)').matches;
// backdrop-filter url(#displace) only bends the backdrop in Chromium; elsewhere it
// silently no-ops, so Safari/Firefox fall back to the edge optics + a touch more blur
// (a deliberately-designed clear-glass chip, never a browser-default pill).
const CHROMIUM = /\b(?:Chrome|Chromium|Edg)\//.test(navigator.userAgent) && !/\b(?:Firefox|FxiOS)\b/.test(navigator.userAgent);

const DEFAULTS = {
  scale: 0.95,        // displacement strength — higher = chunkier, thicker glass
  aberration: 0.5,    // R/B channel spread → visible rainbow / dispersion at the bevel
  magnify: 0.14,      // buttons act as a thick glass LENS — slight convex magnification of the backdrop
  edge: 0.95,         // thick refractive bevel (chunky glass edge)
  dir: 0.32,          // directional prism cast — stronger so the colour bar climbs higher
  activation: 150,
  tilt: 15,           // max degrees the glass dips toward the cursor — strong enough to read the edge geometry
  refractShift: 12,   // px the refraction slides as the glass tilts (keep < lens overscan 14)
  ease: 0.13,         // lower = heavier, silkier settle
  hoverScaleBoost: 1.45, // displacement multiplier while hovered
  maxRes: 220,        // cap generated map resolution (perf)
};

/* ---------- math --------------------------------------------------------- */
const clamp = (v, a, b) => (v < a ? a : v > b ? b : v);
const clamp01 = (v) => (v < 0 ? 0 : v > 1 ? 1 : v);
const lerp = (a, b, t) => a + (b - a) * t;
const smoothstep = (a, b, t) => { t = clamp01((t - a) / (b - a)); return t * t * (3 - 2 * t); };

function sdRoundedBox(px, py, hw, hh, r) {
  const qx = Math.abs(px) - hw + r;
  const qy = Math.abs(py) - hh + r;
  return Math.min(Math.max(qx, qy), 0) + Math.hypot(Math.max(qx, 0), Math.max(qy, 0)) - r;
}

/* ---------- concave-lens displacement map (per element, cached) ----------- */
// single global light: bottom-left of the viewport. Refraction/dispersion is
// cast consistently away from it (toward the top-right).
const LIGHT_DIR = { x: 0.6, y: -0.8 };   // direction the refraction casts

const mapCache = new Map();
// edgeFrac = how thick the refractive bevel is (chunky glass = wide bevel)
// magnify  = 0 for a clear slab; >0 makes the WHOLE area a convex magnifier (chip)
// dirAmt   = uniform directional refraction (the prism cast) added across the body
function lensDataURL(w, h, radius, edgeFrac, magnify, dirAmt) {
  magnify = magnify || 0;
  dirAmt = dirAmt || 0;
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const key = `${w}x${h}x${radius}x${edgeFrac}x${magnify}x${dirAmt}x${dpr}`;
  if (mapCache.has(key)) return mapCache.get(key);

  // generate at DEVICE-pixel density (capped) — backdrop-filter samples at device
  // px, so a 1× map softens the rim/aberration on Retina; this keeps it bold+crisp
  const s = Math.min(dpr, (DEFAULTS.maxRes * dpr) / Math.max(w, h));
  const gw = Math.max(8, Math.round(w * s));
  const gh = Math.max(8, Math.round(h * s));
  const hw = gw / 2, hh = gh / 2;
  const r = clamp(radius * s, 0, Math.min(hw, hh));
  const edge = Math.max(2, edgeFrac * hh);
  const minHalf = Math.min(hw, hh);

  const cv = document.createElement('canvas');
  cv.width = gw; cv.height = gh;
  const ctx = cv.getContext('2d');
  const img = ctx.createImageData(gw, gh);
  const d = img.data;
  const e = 1;

  for (let y = 0; y < gh; y++) {
    for (let x = 0; x < gw; x++) {
      const px = x - hw + 0.5, py = y - hh + 0.5;
      const dist = sdRoundedBox(px, py, hw, hh, r);
      let dvx = 0, dvy = 0;
      if (dist < 0) {
        // 1) thick refractive BEVEL at the rim (the chunky glass edge)
        const depth = -dist;
        const refract = 1 - smoothstep(0, edge, depth);
        if (refract > 0.001) {
          const nx = sdRoundedBox(px + e, py, hw, hh, r) - sdRoundedBox(px - e, py, hw, hh, r);
          const ny = sdRoundedBox(px, py + e, hw, hh, r) - sdRoundedBox(px, py - e, hw, hh, r);
          const nl = Math.hypot(nx, ny) || 1;
          const k = refract * (2 - refract);
          dvx += -(nx / nl) * k;
          dvy += -(ny / nl) * k;
        }
        // 2) convex MAGNIFIER across the whole area (used by the chip). The sampling
        //    offset points INWARD (toward centre) so the item text is genuinely
        //    ENLARGED (the old sign sampled outward → it was minifying, hence "faint").
        //    A near-linear radial profile = closer to a uniform zoom about the centre.
        if (magnify > 0) {
          const rl = Math.hypot(px, py) || 1;
          const rr = Math.min(1, rl / minHalf);
          const m = Math.pow(rr, 0.85) * magnify;
          dvx += (px / rl) * m;
          dvy += (py / rl) * m;
        }
        // 3) uniform DIRECTIONAL refraction — the prism casts toward the light's
        //    opposite, growing toward the bottom (where the colour source sits),
        //    so the solid colour bar disperses up through the body. Faded to ZERO
        //    near every edge so it never samples content OUTSIDE the glass (that
        //    caused a white sliver on the cast-side edge).
        if (dirAmt > 0) {
          const xd = Math.min(x, gw - 1 - x);
          const yd = Math.min(y, gh - 1 - y);
          const eFade = clamp01(xd / (gw * 0.3)) * clamp01(yd / (gh * 0.45));
          const vBias = clamp01((py / hh) * 0.5 + 0.6); // stronger low in the glass
          dvx += LIGHT_DIR.x * dirAmt * vBias * eFade;
          dvy += LIGHT_DIR.y * dirAmt * vBias * eFade;
        }
      }
      const i = (y * gw + x) * 4;
      d[i] = clamp(0.5 - dvx * 0.5, 0, 1) * 255;
      d[i + 1] = 128;
      d[i + 2] = clamp(0.5 - dvy * 0.5, 0, 1) * 255;
      d[i + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);
  const url = cv.toDataURL();
  mapCache.set(key, url);
  return url;
}

/* ---------- per-element SVG filter (chromatic-aberration displacement) ----- */
const SVGNS = 'http://www.w3.org/2000/svg';
let defs;
function ensureDefs() {
  if (defs) return;
  const svg = document.createElementNS(SVGNS, 'svg');
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('width', '0'); svg.setAttribute('height', '0');
  svg.style.cssText = 'position:absolute;width:0;height:0;overflow:hidden;pointer-events:none;';
  defs = document.createElementNS(SVGNS, 'defs');
  svg.appendChild(defs);
  document.body.appendChild(svg);

  // CAUSTIC texture as an SVG MASK image (renders reliably, unlike a generating
  // filter on an empty element). Fractal noise → thin bright bands carried in
  // the ALPHA channel → a "light network" used to mask an accent-coloured fill,
  // so each glass takes its own blue/orange caustics (reads even over white).
  // Two layers (different seeds/scales) give the liquid depth.
  // Alpha = mean(RGB noise) (weights sum to 1 → spans 0..1, centred ~0.5); the
  // feFuncA table spikes the MIDRANGE → thin bright "caustic" veins (a high
  // alpha-sum would clamp to 1 and the table would zero it — empty mask).
  const causticSVG = (seed, bf) =>
    "<svg xmlns='http://www.w3.org/2000/svg' width='240' height='96'>" +
    "<filter id='c'>" +
    `<feTurbulence type='fractalNoise' baseFrequency='${bf}' numOctaves='2' seed='${seed}' stitchTiles='stitch' result='n'/>` +
    "<feColorMatrix in='n' type='matrix' values='0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  .34 .34 .34 0 0' result='l'/>" +
    "<feComponentTransfer in='l' result='b'><feFuncA type='table' tableValues='0 0 .15 .7 1 .7 .15 0 0'/></feComponentTransfer>" +
    "<feGaussianBlur in='b' stdDeviation='.6'/></filter>" +
    "<rect width='240' height='96' filter='url(#c)'/></svg>";
  const url = (s, bf) => 'url("data:image/svg+xml,' + encodeURIComponent(causticSVG(s, bf)) + '")';
  const root = document.documentElement.style;
  root.setProperty('--ecp-caustic', url(11, '0.022 0.034'));
  root.setProperty('--ecp-caustic2', url(4, '0.013 0.024'));
}

function makeFilter(id) {
  ensureDefs();
  const f = document.createElementNS(SVGNS, 'filter');
  f.setAttribute('id', id);
  f.setAttribute('x', '-40%'); f.setAttribute('y', '-40%');
  f.setAttribute('width', '180%'); f.setAttribute('height', '180%');
  f.setAttribute('color-interpolation-filters', 'sRGB');

  const feImage = document.createElementNS(SVGNS, 'feImage');
  feImage.setAttribute('x', '0'); feImage.setAttribute('y', '0');
  feImage.setAttribute('width', '100%'); feImage.setAttribute('height', '100%');
  feImage.setAttribute('preserveAspectRatio', 'none');
  feImage.setAttribute('result', 'MAP');
  f.appendChild(feImage);

  const chan = (scaleVar, mat, res) => {
    const dm = document.createElementNS(SVGNS, 'feDisplacementMap');
    dm.setAttribute('in', 'SourceGraphic');
    dm.setAttribute('in2', 'MAP');
    dm.setAttribute('xChannelSelector', 'R');
    dm.setAttribute('yChannelSelector', 'B');
    dm.setAttribute('result', res + '_D');
    f.appendChild(dm);
    const cm = document.createElementNS(SVGNS, 'feColorMatrix');
    cm.setAttribute('in', res + '_D');
    cm.setAttribute('type', 'matrix');
    cm.setAttribute('values', mat);
    cm.setAttribute('result', res);
    f.appendChild(cm);
    return dm;
  };
  const dmR = chan('R', '1 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 1 0', 'R');
  const dmG = chan('G', '0 0 0 0 0  0 1 0 0 0  0 0 0 0 0  0 0 0 1 0', 'G');
  const dmB = chan('B', '0 0 0 0 0  0 0 0 0 0  0 0 1 0 0  0 0 0 1 0', 'B');

  const blend1 = document.createElementNS(SVGNS, 'feBlend');
  blend1.setAttribute('in', 'G'); blend1.setAttribute('in2', 'B');
  blend1.setAttribute('mode', 'screen'); blend1.setAttribute('result', 'GB');
  f.appendChild(blend1);
  const blend2 = document.createElementNS(SVGNS, 'feBlend');
  blend2.setAttribute('in', 'R'); blend2.setAttribute('in2', 'GB');
  blend2.setAttribute('mode', 'screen'); blend2.setAttribute('result', 'RGB');
  f.appendChild(blend2);
  const blur = document.createElementNS(SVGNS, 'feGaussianBlur');
  blur.setAttribute('in', 'RGB'); blur.setAttribute('stdDeviation', '0.4');
  f.appendChild(blur);

  defs.appendChild(f);
  return { feImage, dmR, dmG, dmB };
}

function setFilterScale(parts, scale, aberration) {
  parts.dmR.setAttribute('scale', scale.toFixed(2));
  parts.dmG.setAttribute('scale', (scale * (1 - aberration)).toFixed(2));
  parts.dmB.setAttribute('scale', (scale * (1 - aberration * 2)).toFixed(2));
}

/* ---------- decorate an element ------------------------------------------ */
let uidSeq = 0;
const ITEMS = [];

function num(el, name, fallback) {
  const v = parseFloat(el.dataset[name]);
  return Number.isFinite(v) ? v : fallback;
}

function decorate(el) {
  if (el.__lg) return el.__lg;
  ensureDefs();
  const uid = 'lg' + (++uidSeq);
  const filterId = uid + '-f';
  const parts = makeFilter(filterId);

  // content → sharp, etched-into-the-glass layer (rides on the tilting plate)
  const content = document.createElement('span');
  content.className = 'lg__content';
  while (el.firstChild) content.appendChild(el.firstChild);

  // STATIC colour source — a bold, crisp bar of pure colour behind the glass
  // at the bottom edge. It never moves; the glass tilts over it.
  const source = document.createElement('span');
  source.className = 'lg__source';
  source.setAttribute('aria-hidden', 'true');

  // FLAT refraction layers (backdrop-filter must NOT be inside a 3D transform):
  //   lens  — backdrop-filter: blur saturate url(#displacement). The url() lives
  //           in backdrop-filter so it bends the backdrop — the static source +
  //           the page — with chromatic aberration.
  //   frost — adaptive white veil.
  const layers = document.createElement('span');
  layers.className = 'lg__layers';
  layers.setAttribute('aria-hidden', 'true');
  const lens = document.createElement('span'); lens.className = 'lg__lens';
  lens.style.setProperty('--lg-filter', `url(#${filterId})`);
  const frost = document.createElement('span'); frost.className = 'lg__frost';
  layers.append(lens, frost);

  // TILTING glass surface — rim + specular overlay. Rotates in 3D on hover
  // (no backdrop-filter here, so 3D is safe). The content tilts with it via the
  // same vars but stays in-flow so it gives the button its size.
  const plate = document.createElement('span');
  plate.className = 'lg__plate';
  plate.setAttribute('aria-hidden', 'true');
  const rim = document.createElement('span'); rim.className = 'lg__rim';
  const chroma = document.createElement('span'); chroma.className = 'lg__chroma';
  const spec = document.createElement('span'); spec.className = 'lg__spec';
  plate.append(rim, chroma, spec);

  // The whole glass (lens + plate + content) tilts as ONE rigid slab, so its edges
  // never separate — a flat lens behind a 3D-rotating surface diverges at the rim
  // and reveals un-refracted page on white. backdrop-filter DOES survive a
  // 3D-transformed ancestor in Chromium (verified). The etched content still rides
  // a deeper translateZ than the plate, so the text parallaxes for real depth.
  const tilt = document.createElement('span');
  tilt.className = 'lg__tilt';
  tilt.append(layers, plate, content);

  el.append(source, tilt);
  el.classList.add('lg-ready');

  const opts = {
    scale: num(el, 'glassScale', DEFAULTS.scale),
    aberration: num(el, 'glassAberration', DEFAULTS.aberration),
    edge: num(el, 'glassEdge', DEFAULTS.edge),
    magnify: num(el, 'glassMagnify', DEFAULTS.magnify),
    dir: num(el, 'glassDir', DEFAULTS.dir),
    activation: num(el, 'glassActivation', DEFAULTS.activation),
    tilt: num(el, 'glassTilt', DEFAULTS.tilt),
    refractShift: num(el, 'glassRefractShift', DEFAULTS.refractShift),
    ease: num(el, 'glassEase', DEFAULTS.ease),
    hoverBoost: num(el, 'glassHoverBoost', DEFAULTS.hoverScaleBoost),
    adaptive: el.dataset.glassAdaptive !== 'off',
  };

  const state = {
    el, uid, parts, opts,
    w: 0, h: 0, radius: 0, isChip: false,
    rx: 0, ry: 0, vrx: 0, vry: 0, shx: 0, shy: 0, lx: 0.5, ly: 0.5, act: 0, press: 0, hover: 0, lum: 1, moving: 0,
    trx: 0, try_: 0, tshx: 0, tshy: 0, tlx: 0.5, tly: 0.5, tact: 0, tpress: 0, thover: 0, tlum: 1, tmoving: 0,
    rect: null, atRest: false, hovering: false,
  };

  el.addEventListener('pointerenter', () => { state.hovering = true; });
  el.addEventListener('pointerleave', () => { state.hovering = false; state.tpress = 0; });
  el.addEventListener('pointerdown', () => { state.tpress = 1; });
  const up = () => { state.tpress = 0; };
  el.addEventListener('pointerup', up);
  el.addEventListener('pointercancel', up);

  el.__lg = state;
  ITEMS.push(state);
  resizeItem(state);
  return state;
}

function resizeItem(s) {
  const r = s.el.getBoundingClientRect();
  s.rect = r;
  const w = Math.round(r.width), h = Math.round(r.height);
  if (!w || !h) return;
  const cs = getComputedStyle(s.el);
  let radius = parseFloat(cs.borderTopLeftRadius) || 16;
  radius = Math.min(radius, Math.min(w, h) / 2);
  if (w === s.w && h === s.h && radius === s.radius) return;
  s.w = w; s.h = h; s.radius = radius;
  const url = lensDataURL(w, h, radius, s.opts.edge, s.opts.magnify, s.opts.dir);
  s.parts.feImage.setAttribute('href', url);
  s.parts.feImage.setAttributeNS('http://www.w3.org/1999/xlink', 'href', url);
  setFilterScale(s.parts, h * s.opts.scale, s.opts.aberration);
}

/* ---------- named lens (for sub-layers like the nav bar, not full .lg) ----- */
const NAMED = [];
function applyLens(el) {
  const r = el.getBoundingClientRect();
  const w = Math.round(r.width), h = Math.round(r.height);
  if (!w || !h) return;
  if (!el.__lens) {
    const id = 'lg-named-' + (++uidSeq);
    el.__lens = { id, parts: makeFilter(id), w: 0, h: 0,
      scale: num(el, 'glassLensScale', 0.3),
      aberration: num(el, 'glassLensAberration', 0.14),
      edge: num(el, 'glassLensEdge', 0.6),
      radius: num(el, 'glassLensRadius', 22) };
    el.style.setProperty('--lg-filter', `url(#${id})`);
    NAMED.push(el);
  }
  const L = el.__lens;
  if (w === L.w && h === L.h) return;
  L.w = w; L.h = h;
  const url = lensDataURL(w, h, L.radius, L.edge, 0, 0.12);
  L.parts.feImage.setAttribute('href', url);
  L.parts.feImage.setAttributeNS('http://www.w3.org/1999/xlink', 'href', url);
  setFilterScale(L.parts, h * L.scale, L.aberration);
}

/* ---------- adaptive opacity: pixel-sample the backdrop -------------------- */
const sampleCanvas = document.createElement('canvas');
sampleCanvas.width = 12; sampleCanvas.height = 6;
const sampleCtx = sampleCanvas.getContext('2d', { willReadFrequently: true });
const bgImageCache = new Map();

function luminanceOf(r, g, b) { return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255; }

function parseColorLuma(str) {
  const m = str.match(/rgba?\(([^)]+)\)/);
  if (!m) return null;
  const p = m[1].split(',').map(parseFloat);
  if (p.length >= 4 && p[3] === 0) return null; // transparent → see through
  return luminanceOf(p[0], p[1], p[2]);
}

function sampleBackdrop(s) {
  // find the topmost meaningful background behind the element's centre
  const r = s.rect;
  if (!r || !r.width) return;
  const cx = r.left + r.width / 2;
  const cy = r.top + r.height / 2;
  s.el.style.pointerEvents = 'none';
  const stack = document.elementsFromPoint(cx, cy);
  s.el.style.pointerEvents = '';
  for (const node of stack) {
    if (node === s.el || s.el.contains(node)) continue;
    const tag = node.tagName;
    try {
      if (tag === 'IMG' || tag === 'VIDEO' || tag === 'CANVAS') {
        const media = tag === 'VIDEO' ? node : node;
        if (tag === 'VIDEO' && node.readyState < 2) continue;
        const mr = node.getBoundingClientRect();
        // map the glass footprint into the media's pixel space
        const natW = node.naturalWidth || node.videoWidth || mr.width;
        const natH = node.naturalHeight || node.videoHeight || mr.height;
        const sxp = clamp((cx - mr.left) / mr.width, 0, 1) * natW;
        const syp = clamp((cy - mr.top) / mr.height, 0, 1) * natH;
        const sw = clamp((r.width / mr.width) * natW, 4, natW);
        const sh = clamp((r.height / mr.height) * natH, 4, natH);
        sampleCtx.drawImage(media, clamp(sxp - sw / 2, 0, natW - sw), clamp(syp - sh / 2, 0, natH - sh), sw, sh, 0, 0, 12, 6);
        const px = sampleCtx.getImageData(0, 0, 12, 6).data;
        let sum = 0, n = 0;
        for (let i = 0; i < px.length; i += 4) { sum += luminanceOf(px[i], px[i + 1], px[i + 2]); n++; }
        s.tlum = sum / n;
        return;
      }
      const cs = getComputedStyle(node);
      const bgImg = cs.backgroundImage;
      if (bgImg && bgImg !== 'none' && bgImg.includes('url(')) {
        const url = bgImg.slice(bgImg.indexOf('url(') + 4).replace(/^["']|["']\)?.*$/g, '');
        const cached = bgImageCache.get(url);
        if (cached === undefined) {
          bgImageCache.set(url, null);
          const im = new Image(); im.crossOrigin = 'anonymous';
          im.onload = () => bgImageCache.set(url, im);
          im.src = url;
        } else if (cached) {
          sampleCtx.drawImage(cached, 0, 0, 12, 6);
          const px = sampleCtx.getImageData(0, 0, 12, 6).data;
          let sum = 0, n = 0;
          for (let i = 0; i < px.length; i += 4) { sum += luminanceOf(px[i], px[i + 1], px[i + 2]); n++; }
          s.tlum = sum / n;
          return;
        }
      }
      const lc = parseColorLuma(cs.backgroundColor);
      if (lc !== null) {
        const hint = node.closest('[data-glass-bg]');
        if (hint) { s.tlum = hint.dataset.glassBg === 'dark' ? 0.08 : 0.96; return; }
        s.tlum = lc; return;
      }
    } catch (_) { /* CORS-tainted media: skip */ }
  }
  // nothing opaque behind → assume the white page
  const hint = s.el.closest('[data-glass-bg]');
  s.tlum = hint ? (hint.dataset.glassBg === 'dark' ? 0.08 : 0.96) : 0.96;
}

/* ---------- pointer + rAF loop ------------------------------------------- */
let mx = -1e4, my = -1e4, pointerSeen = false, rectsDirty = true, tick = 0;
window.addEventListener('pointermove', (e) => { mx = e.clientX; my = e.clientY; pointerSeen = true; }, { passive: true });
window.addEventListener('scroll', () => { rectsDirty = true; }, { passive: true });
window.addEventListener('resize', () => { rectsDirty = true; }, { passive: true });
// recompute when the viewport zooms/DPR changes (pinch-zoom, monitor move)…
if (window.visualViewport) window.visualViewport.addEventListener('resize', () => { rectsDirty = true; }, { passive: true });
// …and once the web font loads (button widths change → stale map / detached source bar)
if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => {
  rectsDirty = true;
  document.querySelectorAll('[data-glass-chip]').forEach((g) => g.__chip_move && g.__chip_move());
});

function updateTargets(s) {
  const r = s.rect; if (!r) return;
  const cx = r.left + r.width / 2, cy = r.top + r.height / 2;
  const dx = mx - cx, dy = my - cy;
  s.tlx = clamp01((mx - r.left) / r.width);
  s.tly = clamp01((my - r.top) / r.height);
  const edx = Math.max(0, Math.abs(dx) - r.width / 2);
  const edy = Math.max(0, Math.abs(dy) - r.height / 2);
  const edge = Math.hypot(edx, edy);
  s.tact = edge > s.opts.activation ? 0 : 1 - edge / s.opts.activation;
  s.thover = s.hovering ? 1 : 0;

  if (s.hovering && !REDUCED_MOTION) {
    // weighted tilt — the glass pane dips under the cursor, refracting light
    // differently. The colour source underneath stays put; only the glass moves.
    const nx = clamp((mx - cx) / (r.width / 2), -1, 1);
    const ny = clamp((my - cy) / (r.height / 2), -1, 1);
    s.try_ = nx * s.opts.tilt;          // rotateY — cursor side dips away
    s.trx = -ny * s.opts.tilt;          // rotateX — cursor side dips away
    s.tshx = -nx * s.opts.refractShift; // refraction slides with the tilt
    s.tshy = -ny * s.opts.refractShift;
  } else {
    s.trx = 0; s.try_ = 0; s.tshx = 0; s.tshy = 0;
  }
}

function applyState(s) {
  const e = s.opts.ease;
  // tilt rides a lightly-underdamped SPRING so the slab settles with weight
  // (slight overshoot/jiggle), like gel — instead of a flat linear glide.
  const sk = REDUCED_MOTION ? 0.5 : 0.14, sd = REDUCED_MOTION ? 0.5 : 0.72;
  s.vrx = (s.vrx + (s.trx - s.rx) * sk) * sd; s.rx += s.vrx;
  s.vry = (s.vry + (s.try_ - s.ry) * sk) * sd; s.ry += s.vry;
  s.shx = lerp(s.shx, s.tshx, e); s.shy = lerp(s.shy, s.tshy, e);
  s.lx = lerp(s.lx, s.tlx, e); s.ly = lerp(s.ly, s.tly, e);
  s.act = lerp(s.act, s.tact, e); s.hover = lerp(s.hover, s.thover, 0.2);
  s.press = lerp(s.press, s.tpress, 0.3); s.lum = lerp(s.lum, s.tlum, 0.12);

  const st = s.el.style;
  if (s.isChip) {
    // chip = a real moving LENS: while it travels it heavily magnifies, disperses and
    // blurs the text it passes over (refraction in motion), then eases to a clear,
    // sharp magnification as it settles. `moving` is DISTANCE-to-target driven (set in
    // frame), so the refraction is strong mid-slide regardless of speed/framerate.
    s.moving = lerp(s.moving, s.tmoving, 0.3);
    const target = s.h * (s.opts.scale + s.moving * (s.opts.moveBoost || 0.7));
    if (s.magCur === undefined) { s.magCur = target; s.magV = 0; }
    s.magV = (s.magV + (target - s.magCur) * 0.3) * 0.66;   // stiffness / damping
    s.magCur += s.magV;
    // dispersion (chromatic spread) and refraction blur both surge with travel
    setFilterScale(s.parts, s.magCur, s.opts.aberration + s.moving * 0.4);
    st.setProperty('--lg-blur', (s.moving * 2.4).toFixed(2) + 'px');
  } else {
    // hover intensifies the lens
    const targetScale = s.h * s.opts.scale * (1 + (s.opts.hoverBoost - 1) * s.hover);
    setFilterScale(s.parts, targetScale, s.opts.aberration);
  }
  st.setProperty('--lg-rx', s.rx.toFixed(2) + 'deg');
  st.setProperty('--lg-ry', s.ry.toFixed(2) + 'deg');
  st.setProperty('--lg-shx', s.shx.toFixed(2) + 'px');
  st.setProperty('--lg-shy', s.shy.toFixed(2) + 'px');
  st.setProperty('--lg-lx', (s.lx * 100).toFixed(2) + '%');
  st.setProperty('--lg-ly', (s.ly * 100).toFixed(2) + '%');
  st.setProperty('--lg-act', s.act.toFixed(3));
  st.setProperty('--lg-hover', s.hover.toFixed(3));
  st.setProperty('--lg-press', s.press.toFixed(3));
  st.setProperty('--lg-lum', s.lum.toFixed(3));
  // dispersion is ALWAYS on for buttons (it shifts with the tilt via --lg-shx/shy,
  // it does not brighten on hover). Only the chip surges, while it travels.
  st.setProperty('--lg-chroma', (s.isChip ? s.moving : 0).toFixed(3));

  s.atRest = s.tact <= 0 && s.tpress === 0 && !s.hovering &&
    Math.abs(s.rx) < 0.02 && Math.abs(s.ry) < 0.02 && Math.abs(s.vrx) < 0.01 && Math.abs(s.vry) < 0.01 &&
    Math.abs(s.act) < 0.002 && s.hover < 0.002 && s.press < 0.002 && Math.abs(s.lum - s.tlum) < 0.01;
}

function frame() {
  tick++;
  if (rectsDirty) {
    for (const s of ITEMS) resizeItem(s);
    for (const el of NAMED) applyLens(el);
    rectsDirty = false;
  }
  // chip refraction tracks its REAL speed: vibrant mid-travel, smoothly fading
  // to clear as the CSS transition decelerates it into place (no two-state jump).
  for (const s of ITEMS) {
    if (!s.isChip) continue;
    const left = s.el.getBoundingClientRect().left;
    // surge by DISTANCE from the seated target, not raw speed → the lens refracts hard
    // through the whole slide and eases to clear as it lands (framerate-independent)
    s.tmoving = (s.targetLeft !== undefined) ? clamp01(Math.abs(left - s.targetLeft) / 55) : 0;
  }
  // adaptive sampling: stagger across items, ~6–8 fps each
  if (tick % 9 === 0) {
    const i = (tick / 9 | 0) % Math.max(1, ITEMS.length);
    const s = ITEMS[i];
    if (s && s.opts.adaptive) sampleBackdrop(s);
  }
  if (pointerSeen) for (const s of ITEMS) updateTargets(s);
  for (const s of ITEMS) {
    if (!s.atRest || s.tact > 0 || s.hovering || s.tpress > 0 || Math.abs(s.lum - s.tlum) > 0.01 ||
        (s.isChip && (s.moving > 0.001 || s.tmoving > 0 || Math.abs(s.magV || 0) > 0.02))) applyState(s);
  }
  requestAnimationFrame(frame);
}

/* ---------- nav chip selector -------------------------------------------- */
function initChipGroup(group) {
  if (group.__chip) return;
  const chip = document.createElement('span');
  chip.className = 'nav-chip';
  chip.setAttribute('aria-hidden', 'true');
  chip.dataset.glassScale = '0.5';      // lighter rim displacement → the label under the chip stays clear (was 0.95)
  chip.dataset.glassMagnify = '0';      // NO convex magnifier → the selected label no longer balloons (was 0.8)
  chip.dataset.glassAberration = '0.28';// subtle rainbow at the rim, not a heavy fringe (was 0.52)
  chip.dataset.glassEdge = '0.6';       // a thin, clean glass bevel
  chip.dataset.glassDir = '0';          // clear glass pill, no directional prism cast
  chip.dataset.glassAdaptive = 'off';
  decorate(chip);
  chip.__lg.isChip = true;
  chip.__lg.opts.moveBoost = 0.3;       // a gentle refraction surge mid-slide, not a zoom (was 0.8)
  group.appendChild(chip);
  group.__chip = chip;

  const items = () => [...group.querySelectorAll('[data-chip-item]')];
  let active = group.querySelector('[data-chip-item].is-active') || items()[0];
  let seated = null;

  function setSeated(item) {
    if (seated === item) return;
    if (seated) seated.classList.remove('is-chipped');
    seated = item;
    if (seated) seated.classList.add('is-chipped');
  }
  // the chip snugly highlights the item it sits on (no enlargement) — a clean clear
  // glass pill that slides between links. A hair over 1.0 gives a little breathing room.
  const CHIP_MAG = 1.04;
  function moveTo(item) {
    if (!item) { chip.style.opacity = '0'; return; }
    const gr = group.getBoundingClientRect();
    const ir = item.getBoundingClientRect();   // item's UNSCALED box (is-chipped applied after)
    const cw = ir.width * CHIP_MAG, ch = ir.height * CHIP_MAG;
    const tx = (ir.left - gr.left) - (cw - ir.width) / 2;
    const ty = (ir.top - gr.top) - (ch - ir.height) / 2;
    chip.style.opacity = '1';
    chip.style.width = cw + 'px';
    chip.style.height = ch + 'px';
    chip.style.transform = `translate(${tx}px, ${ty}px)`;
    if (chip.__lg) chip.__lg.targetLeft = gr.left + tx;   // where the lens is heading → drives the travel surge
    rectsDirty = true;
    setSeated(item);   // refraction vibrancy now follows distance-to-target
  }
  // Hover-preview (follow the cursor, then return to the committed item on leave) is a
  // MOUSE affordance. On touch there is no hover: a tap must commit and the chip must STAY.
  // Firing preview/return for touch pointers is what snapped it back — after the tap, the
  // finger-lift `pointerleave` and the scroll-driven `resize` both re-ran "return to active"
  // while it was mid-commit. So gate preview + return to mouse; on touch the chip moves ONLY
  // via commit() below, which nothing else can override.
  group.addEventListener('pointerover', (e) => {
    if (e.pointerType && e.pointerType !== 'mouse') return;
    const it = e.target.closest('[data-chip-item]');
    if (it && group.contains(it)) moveTo(it, true);
  });
  group.addEventListener('pointerleave', (e) => {
    if (e.pointerType && e.pointerType !== 'mouse') return;
    moveTo(active, true);
  });
  // Commit the selection so the chip settles on the chosen item and STAYS. Commit on
  // pointerdown (fires the instant the finger lands, before any pointerleave) and on click
  // (covers mouse and keyboard-activated links, where no pointer events fire).
  const commit = (it) => { if (it && group.contains(it)) { active = it; moveTo(active); } };
  group.addEventListener('pointerdown', (e) => commit(e.target.closest('[data-chip-item]')));
  items().forEach((it) => it.addEventListener('click', () => commit(it)));
  requestAnimationFrame(() => moveTo(active, false));
  window.addEventListener('resize', () => moveTo(active, false), { passive: true });
  group.__chip_move = () => moveTo(active, false);
}

/* ---------- boot --------------------------------------------------------- */
function init(root = document) {
  ensureDefs();
  if (!CHROMIUM) document.documentElement.classList.add('lg-nodisp');
  root.querySelectorAll('[data-glass-lens]').forEach(applyLens);
  root.querySelectorAll('[data-liquid-glass]').forEach(decorate);
  root.querySelectorAll('[data-glass-chip]').forEach(initChipGroup);
  if (!init.__running) { init.__running = true; requestAnimationFrame(frame); }
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
else init();

window.EndureGlass = { init, decorate };
export { init, decorate };
